"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Flyout = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _reactPopper = require("react-popper");
var _useTheme = require("../../theme/utils/useTheme");
var _StackOrder = require("../../util/StackOrder");
var _LayerContext = require("../../util/layerContext/LayerContext");
var _portal = require("../portal");
var _FlyoutArrow = require("./styled/FlyoutArrow");
var _FlyoutContainer = require("./styled/FlyoutContainer");
/**
 * The Flyout shows a floating content area on screen, positioning it next to
 * the given target element. It detects screen edges and will re-position itself
 * to another side of the target element if it gets too close to an edge.
 *
 * It exposes props that control its visibility and is leveraged by components
 * such as Tooltip and FlyoutButton.
 */

/**
 * This is the only piece that Popper needs to position itself.
 * To support virtual elements, we'll only require this interface for
 * the target element.
 */

function thingIsRefObject(thing) {
  return Object.prototype.hasOwnProperty.call(thing, "current");
}
function thingIsLikeHTMLElementEnough(thing) {
  return "getBoundingClientRect" in thing;
}
function elementOrNull(elementRefOrNull) {
  if (!elementRefOrNull) {
    return null;
  } else if (thingIsRefObject(elementRefOrNull)) {
    return elementRefOrNull.current;
  } else if (thingIsLikeHTMLElementEnough(elementRefOrNull)) {
    // This is kind of a weird check, but we want to allow fake elements to be passed in here
    // that just satisfy popper's requirements on a bounding rect.
    // This is for test and to allow a flyout to be free e.g. follow the mouse or some other feature.
    // https://popper.js.org/docs/v2/virtual-elements/
    return elementRefOrNull;
  }
  return null;
}
const Flyout = /*#__PURE__*/(0, _react.forwardRef)((_ref, providedRef) => {
  let {
    arrowColor = "transparent",
    children,
    clientId,
    components = {},
    hasArrow = false,
    isOpen,
    offset,
    onCloseRequested,
    placement = "auto",
    popperProps,
    portalProps,
    targetElement,
    role = "dialog",
    style,
    updateKey,
    closeOnEscape = true,
    closeOnScroll = false,
    ignoreStackOrder = false,
    ...rest
  } = _ref;
  const layerZIndex = (0, _LayerContext.useLayerZIndex)(role === "tooltip" ? "tooltip" : undefined);

  // Using state vs RefObject since Popper needs to re-render once its
  // refs are available.
  const [containerElement, setContainerElement] = (0, _react.useState)(null);
  (0, _StackOrder.useStackOrder)({
    // Generally, isOpen is enough here, though we'll allow this to be overridden in case
    // someone wants to manually control the stack order more carefully. I'm looking at you,
    // SelectV2.
    isOpen: isOpen && !ignoreStackOrder,
    onCloseRequested,
    shouldCloseOnEscape: closeOnEscape
  });

  // Sync the content element back to the provided ref (if there is one).
  const setElementRef = (0, _react.useCallback)(newContainerElement => {
    setContainerElement(newContainerElement);
  }, []);
  const containerElementRef = (0, _lodestarUtils.useForkRef)(providedRef, setElementRef);

  // One more "ref" for the arrow element. Again, popper needs to re-render
  // if this element changes.
  const [arrowElement, setArrowElement] = (0, _react.useState)(null);
  const target = elementOrNull(targetElement);
  const {
    styles,
    attributes,
    state,
    update
  } = (0, _reactPopper.usePopper)(target, containerElement, {
    //strategy: 'fixed', TODO: v5 - enable this by default.
    //Hesitant to enable this as of yet. It's not likely, but possible that users are leveraging the default absolute positioning.
    //I think the most prudent thing to do is to wait for a major version to change this. This fixes the
    //scrolling issue with the flyout. https://app.smartsheet.com/sheets/J26gVQgGrGQ64wq5q9gRRMr2QcCGV5J8CMVhrgV1?rowId=5886713693507460
    //Strangely the problem is only evident in the DatePicker and ToggleTip components. It's fixed in those components via the popperProps prop.
    //If we notice unintended scrolling in other components using Flyout, we should turn this on prior to v5.
    ...popperProps,
    placement,
    modifiers: [{
      name: "offset",
      options: {
        offset: [offset?.skidding || 0, offset?.distance || 0]
      }
    }, ...(popperProps?.modifiers || []), {
      name: "arrow",
      options: {
        element: arrowElement
      }
    }]
  });
  (0, _react.useEffect)(() => {
    if (closeOnScroll && isOpen && state?.scrollParents.reference) {
      let closeRequested = false;
      const handleCloseOnScroll = () => {
        if (!closeRequested) {
          closeRequested = true;
          onCloseRequested();
        }
      };
      const scrollParents = state.scrollParents.reference;
      scrollParents.forEach(scrollParent => {
        scrollParent.addEventListener("scroll", handleCloseOnScroll, {
          passive: true
        });
      });
      return () => {
        scrollParents.forEach(scrollParent => {
          scrollParent.removeEventListener("scroll", handleCloseOnScroll);
        });
      };
    }
  }, [closeOnScroll, onCloseRequested, state?.scrollParents, isOpen]);

  // Because the target and container elements might not be instantiated immediately,
  // we need to fire off an update post-render once they are.
  // This solves issues where the flyout and arrow aren't properly positioned the first render.
  // This *should* only happen once as `update` is initially null but is defined once after the initial render.
  (0, _react.useEffect)(() => {
    if (update) {
      void update();
    }
  }, [update, updateKey]);
  const FlyoutArrow = components.FlyoutArrow || _FlyoutArrow.FlyoutArrow;
  if (!FlyoutArrow.displayName) {
    FlyoutArrow.displayName = _FlyoutArrow.FlyoutArrow.displayName;
  }
  const FlyoutContainer = components.FlyoutContainer || _FlyoutContainer.FlyoutContainer;
  if (!FlyoutContainer.displayName) {
    FlyoutContainer.displayName = _FlyoutContainer.FlyoutContainer.displayName;
  }
  const Portal = components && components.Portal || _portal.Portal;
  const getArrowProps = () => ({
    backgroundColor: arrowColor,
    placement: state?.placement || "auto",
    ref: setArrowElement,
    style: styles.arrow,
    // This attribute is only for test.
    ["data-flyout-arrow"]: "true",
    ...attributes.arrow
  });
  const isChildrenFunction = typeof children === "function";

  // Popper requires 2 renders to show itself in the right spot:
  //
  // 1. initial render at 0,0 to get its width and height.
  // 2. a follow-up render to move the popup to the correct location, given the measurements
  //    from #1.
  //
  // We set opacity to 0 to prevent the user noticing this initial render. It is hard to
  // reproduce and only happens in some environments. That said, it has been consistently
  // reproduced in those environments: storybook on some machines.
  const ref = (0, _react.useRef)(true);
  const firstRender = ref.current;
  ref.current = false;
  const popperOpacity = firstRender ? 0 : 1;
  const {
    cssVariables
  } = (0, _useTheme.useTheme)();
  return isOpen && /*#__PURE__*/_react.default.createElement(Portal
  // Ensure we allow overriding this z-index manually.
  // We'd love to have Flyout use layers["flyout"] by default, but that would be
  // a breaking change since everyone has already manually specified z-indexes
  // everywhere from 3 to 490. We'll have to do this in a major version bump.
  // See https://git.lab.smartsheet.com/team-ui-engineering/lodestar-core/-/issues/501
  , (0, _extends2.default)({
    zIndex: layerZIndex
  }, portalProps), /*#__PURE__*/_react.default.createElement(_LayerContext.LayerContextProvider, {
    defaultLayer: "flyout"
  }, /*#__PURE__*/_react.default.createElement(FlyoutContainer, (0, _extends2.default)({
    style: {
      opacity: popperOpacity,
      ...style,
      ...styles.popper,
      // On the first render, we don't want to mess with the document's
      // scroll positioning at all. This can happen when focus is moved
      // into the Flyout on the first render before it has properly
      // positioned itself. Using position: fixed will allow the Flyout to
      // render at 0,0 of the current viewport, not the document's 0,0.
      // Careful! Don't actually add the position attribute if it's not
      // the first render. We don't want to mess up any styles by setting
      // this to a bad value. The styles from styles.popper provide a
      // "position" as well.
      ...(firstRender ? {
        position: "fixed"
      } : {}),
      ...cssVariables
    },
    ref: containerElementRef,
    "data-client-id": clientId || undefined,
    role: role || undefined
  }, attributes.popper, rest), typeof children === "function" ? children({
    getArrowProps
  }) : children, !isChildrenFunction && hasArrow && /*#__PURE__*/_react.default.createElement(FlyoutArrow, getArrowProps())))) || null;
});
exports.Flyout = Flyout;
Flyout.displayName = "Flyout";
//# sourceMappingURL=Flyout.js.map